import React, { useState } from 'react';
import { TrendingUp, Plus, Edit2, Trash2, Database, Users, Coins, X, Save, Power } from 'lucide-react';
import { useStaking, StakingProduct } from '@/shared/hooks/useStaking';
import { seedStakingProducts } from '@/shared/utils/seedStaking';
import { cn } from '@/shared/lib/utils';
import { formatDisplayName } from '@/shared/utils/formatters';

const AdminStakingPage = () => {
    const { products, myStakings, allStakings, loading, addProduct, updateProduct, deleteProduct, cancelStaking } = useStaking();
    const [activeTab, setActiveTab] = useState<'products' | 'participations'>('products');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState<Partial<StakingProduct> | null>(null);

    // Form states
    const [formData, setFormData] = useState<Partial<StakingProduct>>({
        name: '',
        asset: 'ETH',
        apy: 5.0,
        minAmount: 0.1,
        duration: 30,
        status: 'Active',
        description: ''
    });

    const handleOpenModal = (product?: StakingProduct) => {
        if (product) {
            setEditingProduct(product);
            setFormData(product);
        } else {
            setEditingProduct(null);
            setFormData({
                name: '',
                asset: 'ETH',
                apy: 5.0,
                minAmount: 0.1,
                duration: 30,
                status: 'Active',
                description: ''
            });
        }
        setIsModalOpen(true);
    };

    const handleSave = async () => {
        if (editingProduct?.id) {
            await updateProduct(editingProduct.id, formData);
        } else {
            await addProduct(formData);
        }
        setIsModalOpen(false);
    };

    const handleSeed = async () => {
        if (window.confirm('기존 상품을 삭제하고 샘플 3종을 생성하시겠습니까?')) {
            await seedStakingProducts();
        }
    };

    const totalStats = {
        activeProducts: products.filter(p => p.status === 'Active').length,
        totalStaked: products.reduce((acc, p) => acc + (p.totalStaked || 0), 0),
        totalParticipants: products.reduce((acc, p) => acc + (p.participantCount || 0), 0),
    };

    if (loading) return <div className="p-8 text-white uppercase font-black animate-pulse">스테이킹 시스템 로딩중...</div>;

    return (
        <div className="space-y-8 animate-in fade-in duration-500">
            {/* Header & Stats */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6">
                <div>
                    <h2 className="text-3xl font-black tracking-tight text-white uppercase italic">스테이킹 관리</h2>
                    <p className="text-slate-400 text-sm font-medium mt-1 uppercase tracking-widest">실시간 상품 관리 및 수익률 제어</p>
                </div>
                <div className="flex items-center gap-4">
                    <button
                        onClick={handleSeed}
                        className="flex items-center gap-2 px-6 py-3 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl font-bold transition-all"
                    >
                        <Database className="h-4 w-4" />
                        기본 샘플 생성
                    </button>
                    <button
                        onClick={() => handleOpenModal()}
                        className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-bold transition-all shadow-lg shadow-blue-600/20 active:scale-95"
                    >
                        <Plus className="h-5 w-5" />
                        신규 상품 추가
                    </button>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-2 border-b border-slate-800 pb-px">
                <button
                    onClick={() => setActiveTab('products')}
                    className={cn(
                        "px-8 py-4 font-black uppercase tracking-widest text-[11px] border-b-2 transition-all",
                        activeTab === 'products' ? "border-blue-500 text-blue-500 bg-blue-500/5" : "border-transparent text-slate-500 hover:text-slate-300"
                    )}
                >
                    상품 목록 관리
                </button>
                <button
                    onClick={() => setActiveTab('participations')}
                    className={cn(
                        "px-8 py-4 font-black uppercase tracking-widest text-[11px] border-b-2 transition-all",
                        activeTab === 'participations' ? "border-blue-500 text-blue-500 bg-blue-500/5" : "border-transparent text-slate-500 hover:text-slate-300"
                    )}
                >
                    유저 참여 내역 관리
                </button>
            </div>

            {activeTab === 'products' ? (
                <>
                    {/* Stats Bar */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {[
                            { label: '활성 상품 수', value: totalStats.activeProducts, icon: TrendingUp, color: 'text-blue-500' },
                            { label: '총 예치 수량(합계)', value: totalStats.totalStaked.toLocaleString(), icon: Coins, color: 'text-emerald-500' },
                            { label: '누적 참여자 수', value: totalStats.totalParticipants, icon: Users, color: 'text-amber-500' },
                        ].map((stat, idx) => (
                            <div key={idx} className="bg-slate-900/50 border border-slate-800 p-6 rounded-3xl flex items-center gap-4">
                                <div className={cn("p-4 rounded-2xl bg-slate-800", stat.color)}>
                                    <stat.icon className="h-6 w-6" />
                                </div>
                                <div>
                                    <p className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{stat.label}</p>
                                    <p className="text-2xl font-black text-white tabular-nums">{stat.value}</p>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Product Table */}
                    <div className="bg-slate-950/50 border border-slate-800 rounded-[2rem] overflow-hidden">
                        <table className="w-full text-left border-collapse">
                            <thead>
                                <tr className="border-b border-slate-800 bg-slate-900/50">
                                    <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500">상품 정보 (자산)</th>
                                    <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">연이율 (APY)</th>
                                    <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">예치 기간</th>
                                    <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">총 예치량</th>
                                    <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">상태</th>
                                    <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">관리</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-800/50">
                                {products.map((p) => (
                                    <tr key={p.id} className="hover:bg-slate-800/30 transition-colors group">
                                        <td className="px-8 py-6">
                                            <div className="flex items-center gap-3">
                                                <div className="w-10 h-10 rounded-xl bg-slate-800 flex items-center justify-center font-black text-blue-500">
                                                    {p.asset?.slice(0, 2) || '??'}
                                                </div>
                                                <div>
                                                    <p className="font-bold text-white uppercase leading-none">{p.name}</p>
                                                    <p className="text-[10px] font-medium text-slate-500 mt-1 uppercase tracking-widest">{p.asset || 'N/A'}</p>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <span className="text-lg font-black text-emerald-500 tabular-nums">{p.apy}%</span>
                                        </td>
                                        <td className="px-8 py-6 text-center text-slate-300 font-bold uppercase tracking-tighter">
                                            {p.duration === 0 ? '자유 예치' : `${p.duration}일`}
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <p className="font-black text-white tabular-nums">{(p.totalStaked || 0).toLocaleString()}</p>
                                            <p className="text-[9px] text-slate-500 font-bold uppercase">참여자 {p.participantCount}명</p>
                                        </td>
                                        <td className="px-8 py-6 text-center">
                                            <button
                                                onClick={() => updateProduct(p.id, { status: p.status === 'Active' ? 'Inactive' : 'Active' })}
                                                className={cn(
                                                    "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter transition-all",
                                                    p.status === 'Active' ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/20' : 'bg-slate-500/10 text-slate-500 border border-slate-700'
                                                )}
                                            >
                                                {p.status === 'Active' ? '활성' : '비활성'}
                                            </button>
                                        </td>
                                        <td className="px-8 py-6 text-right">
                                            <div className="flex items-center justify-end gap-2">
                                                <button
                                                    onClick={() => handleOpenModal(p)}
                                                    className="p-2 text-slate-500 hover:text-white hover:bg-slate-800 rounded-lg transition-all"
                                                >
                                                    <Edit2 className="h-4 w-4" />
                                                </button>
                                                <button
                                                    onClick={() => { if (window.confirm('상품을 삭제하시겠습니까?')) deleteProduct(p.id) }}
                                                    className="p-2 text-slate-500 hover:text-red-500 hover:bg-red-500/10 rounded-lg transition-all"
                                                >
                                                    <Trash2 className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            ) : (
                <div className="bg-slate-950/50 border border-slate-800 rounded-[2rem] overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="border-b border-slate-800 bg-slate-900/50">
                                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500">유저 ID / 참여 상품</th>
                                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">예치 수량</th>
                                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">참여 일자</th>
                                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-center">상태</th>
                                <th className="px-8 py-5 text-[10px] font-black uppercase tracking-widest text-slate-500 text-right">관리</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-800/50">
                            {allStakings.map((s) => (
                                <tr key={s.id} className="hover:bg-slate-800/30 transition-colors">
                                    <td className="px-8 py-6">
                                        <p className="font-black text-blue-500 text-xs tracking-tighter uppercase">{formatDisplayName({ id: s.userId })}</p>
                                        <p className="font-bold text-white uppercase text-sm mt-1">{s.productName}</p>
                                    </td>
                                    <td className="px-8 py-6 text-center">
                                        <p className="font-black text-white tabular-nums font-lg">{s.amount.toLocaleString()} <span className="text-slate-500 text-[10px]">{s.asset}</span></p>
                                    </td>
                                    <td className="px-8 py-6 text-center text-slate-400 font-bold text-xs">
                                        {s.stakedAt?.toDate().toLocaleDateString()}
                                    </td>
                                    <td className="px-8 py-6 text-center">
                                        <span className={cn(
                                            "px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-tighter",
                                            s.status === 'Active' ? 'bg-blue-500/10 text-blue-500 border border-blue-500/20' : 'bg-slate-500/10 text-slate-500 border border-slate-800'
                                        )}>
                                            {s.status === 'Active' ? '활성' : '종료됨'}
                                        </span>
                                    </td>
                                    <td className="px-8 py-6 text-right">
                                        {s.status === 'Active' && (
                                            <button
                                                onClick={async () => {
                                                    const refundEstimate = s.amount * 0.85;
                                                    if (window.confirm(`[관리자 강제 해지]\n해당 참여 건을 강제 취소하시겠습니까?\n\n- 해지 수수료: 15% (원금 차감)\n- 최종 환불액: ${refundEstimate.toLocaleString()} ${s.asset}\n\n시스템에 의해 즉시 처리됩니다.`)) {
                                                        try {
                                                            await cancelStaking(s);
                                                            alert(`강제 취소 및 환불 처리가 완료되었습니다. (15% 수수료 공제 후 ${refundEstimate.toLocaleString()} ${s.asset} 환불됨)`);
                                                        } catch (e: any) {
                                                            alert('처리 중 오류 발생: ' + e.message);
                                                        }
                                                    }
                                                }}
                                                className="px-4 py-2 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-[10px] font-black uppercase tracking-widest hover:bg-red-500 hover:text-white transition-all"
                                            >
                                                강제 취소/환불
                                            </button>
                                        )}
                                    </td>
                                </tr>
                            ))}
                            {allStakings.length === 0 && (
                                <tr>
                                    <td colSpan={5} className="px-8 py-20 text-center text-slate-500 font-bold uppercase tracking-widest">
                                        아직 참여 내역이 없습니다.
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Add/Edit Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-in fade-in duration-300">
                    <div className="bg-slate-900 border border-slate-800 w-full max-w-xl rounded-[2.5rem] overflow-hidden shadow-2xl">
                        <div className="px-8 py-6 border-b border-slate-800 flex items-center justify-between bg-slate-900/50">
                            <h3 className="text-xl font-black text-white uppercase italic tracking-tight">
                                {editingProduct ? '상품 정보 수정' : '신규 스테이킹 상품 등록'}
                            </h3>
                            <button onClick={() => setIsModalOpen(false)} className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-full transition-all">
                                <X className="h-5 w-5" />
                            </button>
                        </div>
                        <div className="p-8 space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                                <div className="space-y-2 col-span-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">상품명</label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white font-bold focus:outline-none focus:border-blue-500 transition-colors"
                                        placeholder="상품명을 입력하세요"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">기초 자산</label>
                                    <input
                                        type="text"
                                        value={formData.asset}
                                        onChange={e => setFormData({ ...formData, asset: e.target.value.toUpperCase() })}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white font-bold focus:outline-none focus:border-blue-500 transition-colors"
                                        placeholder="EX: ETH, USDT"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">연이율 (APY %)</label>
                                    <input
                                        type="number"
                                        value={formData.apy}
                                        onChange={e => setFormData({ ...formData, apy: Number(e.target.value) })}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white font-bold focus:outline-none focus:border-blue-500 transition-colors"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">최소 예치 수량</label>
                                    <input
                                        type="number"
                                        value={formData.minAmount}
                                        onChange={e => setFormData({ ...formData, minAmount: Number(e.target.value) })}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white font-bold focus:outline-none focus:border-blue-500 transition-colors"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">예치 기간 (일, 0=자유 예치)</label>
                                    <input
                                        type="number"
                                        value={formData.duration}
                                        onChange={e => setFormData({ ...formData, duration: Number(e.target.value) })}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white font-bold focus:outline-none focus:border-blue-500 transition-colors"
                                    />
                                </div>
                                <div className="space-y-2 col-span-2">
                                    <label className="text-[10px] font-black uppercase tracking-widest text-slate-500">상품 설명</label>
                                    <textarea
                                        value={formData.description}
                                        onChange={e => setFormData({ ...formData, description: e.target.value })}
                                        className="w-full bg-slate-950 border border-slate-800 rounded-xl px-4 py-3 text-white font-medium focus:outline-none focus:border-blue-500 transition-colors h-24 resize-none"
                                        placeholder="상품에 대한 상세 설명을 입력하세요"
                                    />
                                </div>
                                <button
                                    onClick={handleSave}
                                    className="w-full flex items-center justify-center gap-2 py-4 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl font-black uppercase tracking-widest transition-all shadow-lg shadow-emerald-900/20 active:scale-[0.98]"
                                >
                                    <Save className="h-5 w-5" />
                                    {editingProduct ? '수정 사항 저장' : '등록 완료'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminStakingPage;
